import clsx from "clsx";
import type { JSX, ReactNode } from "react";
import Select, { type MultiValue } from "react-select";
import type { ChoiceOption } from "./types";

export const FilterSection = ({
  title,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): JSX.Element => {
  return (
    <section className="space-y-3">
      <h3 className="text-[11px] font-bold uppercase tracking-wide text-(--text-muted)">
        {title}
      </h3>
      {children}
    </section>
  );
};

const FieldLabel = ({
  label,
}: {
  label: string;
  helperText?: string | undefined;
}): JSX.Element => (
  <span className="text-[12px] font-semibold text-(--text-secondary)">
    {label}
  </span>
);

export const MultiSelectField = <T extends string | number>({
  label,
  helperText,
  options,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  helperText?: string;
  options: ChoiceOption<T>[];
  value: T[];
  onChange: (nextValue: T[]) => void;
  placeholder?: string;
}): JSX.Element => {
  const selectedOptions = options.filter((option) =>
    value.includes(option.value),
  );
  const controlClassName = (isFocused: boolean): string =>
    clsx(
      "min-h-10 rounded-(--radius-md) border bg-(--surface-1) px-3 text-[13px] text-(--text-primary) transition",
      isFocused
        ? "border-(--brand-primary)"
        : "border-(--border-subtle) hover:border-(--border-strong)",
    );
  const optionClassName = (isSelected: boolean, isFocused: boolean): string =>
    clsx("cursor-pointer rounded-(--radius-md) px-3 py-2 text-[13px] transition", {
      "bg-(--pastel-violet) text-(--brand-primary) font-semibold": isSelected,
      "bg-(--surface-2) text-(--text-primary)": !isSelected && isFocused,
      "text-(--text-secondary)": !isSelected && !isFocused,
    });
  const dropdownIndicatorClassName = (isFocused: boolean): string =>
    clsx("px-1 text-(--text-secondary) transition", {
      "text-(--text-primary)": isFocused,
    });

  return (
    <label className="block space-y-2">
      <FieldLabel label={label} helperText={helperText} />
      <Select<ChoiceOption<T>, true>
        isMulti
        isSearchable
        unstyled
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        backspaceRemovesValue={false}
        tabSelectsValue={false}
        options={options}
        value={selectedOptions}
        placeholder={placeholder ?? "Search and select..."}
        noOptionsMessage={() => "No matching options"}
        menuPlacement="auto"
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 1600,
          }),
        }}
        onChange={(nextValue: MultiValue<ChoiceOption<T>>) => {
          onChange(nextValue.map((option) => option.value));
        }}
        classNames={{
          control: (state) => controlClassName(state.isFocused),
          valueContainer: () => "gap-1.5 py-1.5",
          placeholder: () => "text-[13px] text-(--text-muted)",
          input: () => "m-0 p-0 text-[13px] text-(--text-primary)",
          menu: () =>
            "mt-2 overflow-hidden rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1)",
          menuList: () => "max-h-[248px] p-1.5",
          option: (state) => optionClassName(state.isSelected, state.isFocused),
          multiValue: () =>
            "rounded-(--radius-sm) bg-(--pastel-violet)",
          multiValueLabel: () =>
            "px-2 py-0.5 text-[12px] font-semibold text-(--brand-primary)",
          multiValueRemove: () =>
            "flex items-center rounded-r-(--radius-sm) px-1.5 text-(--brand-primary) transition hover:bg-(--brand-primary) hover:text-(--brand-primary-contrast)",
          indicatorsContainer: () => "gap-1",
          dropdownIndicator: (state) =>
            dropdownIndicatorClassName(state.isFocused),
          clearIndicator: () =>
            "px-1 text-(--text-muted) transition hover:text-(--text-primary)",
          indicatorSeparator: () => "hidden",
          noOptionsMessage: () =>
            "px-3 py-2 text-[13px] text-(--text-muted)",
        }}
      />
    </label>
  );
};
